import React, { ChangeEvent, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../context';
import { UserSelector } from '../../components/UserSelector';
import { IAccount, ISortBy, ORDER } from '../../resource';
import {
  Box,
  Button,
  Pagination,
  CircularProgress,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Edit, Delete, Visibility, ContentCopy, Refresh as RecoverIcon, DeleteForever as DeleteForeverIcon } from '@mui/icons-material';

import apis from '../../apis';
import { ROUTES } from '../../contants';
import { PlusIcon } from '../../assets/icons';
import { useToastContext } from '../../components/Toast/toastContext';
import { WidgetPreviewDialog } from '../../components/Dialogs/WidgetPreview';
import {useAlertContext} from '../../components/Alert/alertContext';
import {DuplicateWidget} from '../../components/Dialogs/DuplicateWidget';
import { IColumn, Table } from '../../components/Table';
import moment from 'moment/moment';
import { useDebounce } from '../../hooks/useDebounce';

// Utils
import { useUrlParams } from '../../utils/urlParams';



export const ManageWidgetsPage = () => {
  const navigate = useNavigate();
  
  // URL params hook
  const { updateUrlParams, updateUrlParamsAfterApiResponse, initializeFilters, getUrlParams, restoreFromStorage } = useUrlParams('manageWidgets');
  
  // Context
  const { isAdmin, users, account, showDeleted, setShowDeleted, retentionConfig, search, setSearch } = useContext(AppContext);
  const debouncedSearch = useDebounce(search);
  const { activeToast } = useToastContext();
  const { activeAlert } = useAlertContext();
  const activeToastRef = useRef(activeToast);

  // Update the ref when it changes
  useEffect(() => {
    activeToastRef.current = activeToast;
  }, [activeToast]);

  const [customer, setCustomer] = useState<IAccount | null | undefined>(null);

  // Handle customer selection change
  const handleCustomerChange = useCallback((newCustomer: IAccount | null) => {
    console.log('Customer changed to:', newCustomer);
    setCustomer(newCustomer);
    setCurrentPage(1); // Reset to first page when customer changes
    updateUrlParams({ 
      customer: newCustomer?.id || undefined,
      page: 1 
    });
  }, [updateUrlParams]);
  const [loading, setLoading] = useState(false);
  const [widgets, setWidgets] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [dataPage, setDataPage] = useState<number>(1); // Track which page the current data belongs to
  const [sort, setSort] = useState<ISortBy>({
    field: 'name',
    order: ORDER.ASC,
  });
  const [selectedWidget, setSelectedWidget] = useState<any | null>(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const latestRequestId = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Initialize filters from URL or localStorage
  useEffect(() => {
    console.log('Initializing ManageWidgets filters...');
    
    // Get current URL parameters
    const urlParams = getUrlParams();
    console.log('Current URL params:', urlParams);
    
    // Get stored filters
    const storedParams = restoreFromStorage();
    console.log('Stored params:', storedParams);
    
    // Determine which parameters to use
    let finalParams;
    
    if (Object.keys(urlParams).length > 0) {
      // URL has parameters - use them
      console.log('Using URL parameters');
      finalParams = urlParams;
    } else if (Object.keys(storedParams).length > 0) {
      // No URL params but stored params exist - use stored params
      console.log('Using stored parameters');
      finalParams = storedParams;
    } else {
      // No stored params - use defaults
      console.log('Using default parameters');
      finalParams = {
        page: 1,
        perPage: 20,
        sort: 'name_asc',
        archived: false,
      };
    }
    
    console.log('Final params to apply:', finalParams);
    
    // Apply the final parameters
    setCurrentPage(finalParams.page || 1);
    setPerPage(finalParams.perPage || 20);
    setSort({
      field: finalParams.sort?.split('_')[0] || 'name',
      order: finalParams.sort?.split('_')[1] === 'desc' ? ORDER.DESC : ORDER.ASC,
    });
    
    if (finalParams.archived !== undefined) {
      setShowDeleted(finalParams.archived);
    }
    
    // Handle search parameter - only restore from URL params, not localStorage
    if (Object.keys(urlParams).length > 0 && urlParams.search !== undefined && urlParams.search !== '') {
      setSearch(urlParams.search);
    }
    
    // Handle customer parameter for admin users
    if (isAdmin && finalParams.customer) {
      const customerUser = users.value.find(user => user.id === finalParams.customer);
      if (customerUser) {
        setCustomer(customerUser);
      }
    }
    
    setIsInitialized(true);
  }, [isAdmin, users.value]);

  // Reusable fetch function
  const fetchWidgets = useCallback(async (showLoading = true) => {
    // For non-admin users, fetch when account is available
    if (!isAdmin && !account) return;

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    latestRequestId.current += 1;
    const requestId = latestRequestId.current;

    if (showLoading) {
      setLoading(true);
    }

    const options = {
      limit: perPage,
      skip: (currentPage - 1) * perPage,
      sort: {
        [sort.field]: sort.order === ORDER.ASC ? 'asc' : 'desc',
      },
      search: debouncedSearch || '',
      archived: showDeleted ? 1 : 0,
    };

    console.log('Fetching widgets with options:', options, 'currentPage:', currentPage);

    // For non-admin users, use account directly; for admin users, use selected customer
    const targetCustomer = isAdmin ? customer : account;
    console.log('Target customer:', targetCustomer ? `${targetCustomer.firstName} ${targetCustomer.lastName} (${targetCustomer.id})` : 'None selected');

    try {
      if (targetCustomer) {
        console.log('Calling getCdnWidget for customer:', targetCustomer.id);
        const res = await apis.cdn.getCdnWidget(targetCustomer.id, { options }, controller.signal);
        console.log('API response:', res);
        if (res && Array.isArray(res.data)) {
          setWidgets(res.data);
          setTotalCount(res.pagination?.total || res.data.length);
          setDataPage(currentPage); // Set the page that this data belongs to
        } else if (res && Array.isArray(res)) {
          // Handle case where response is directly an array
          setWidgets(res);
          setTotalCount(res.length);
          setDataPage(currentPage); // Set the page that this data belongs to
        } else {
          setWidgets([]);
          setTotalCount(0);
        }
      } else {
        // No customer selected (for admin users) or no account (for non-admin users)
        console.log('Calling searchCdnWidget (no customer selected)');
        const res = await apis.cdn.searchCdnWidget({ options }, controller.signal);
        console.log('API response:', res);
        if (res && Array.isArray(res.data)) {
          setWidgets(res.data);
          setTotalCount(res.pagination?.total || res.data.length);
          setDataPage(currentPage); // Set the page that this data belongs to
        } else if (res && Array.isArray(res)) {
          // Handle case where response is directly an array
          setWidgets(res);
          setTotalCount(res.length);
          setDataPage(currentPage); // Set the page that this data belongs to
        } else {
          setWidgets([]);
          setTotalCount(0);
        }
      }
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.message?.includes('canceled') || err?.canceled) {
        // Request was cancelled, do nothing
        return;
      }
      console.log('catch>>>', err);
      setWidgets([]);
      setTotalCount(0);
      // Show real errors only
      activeToastRef.current({
        type: 'error',
        text: err.message || 'Failed to fetch widgets',
      });
    } finally {
      // Only set loading to false if this is still the current request
      if (requestId === latestRequestId.current && showLoading) {
        setLoading(false);
      }
    }
  }, [isAdmin, account, customer, perPage, currentPage, sort, debouncedSearch, showDeleted]);

  // Fetch data when dependencies change
  useEffect(() => {
    if (!isInitialized) return;
    fetchWidgets();
  }, [isInitialized, fetchWidgets]);

  // On search changed - update URL after successful API response
  useEffect(() => {
    if (isInitialized && debouncedSearch !== undefined) {
      // Update URL params after a short delay to allow API response
      setTimeout(() => {
        updateUrlParamsAfterApiResponse({ search: debouncedSearch });
      }, 100);
    }
  }, [debouncedSearch, isInitialized]);

  const handleOpenDetailModal = (widget) => {
    setSelectedWidget(widget);
    setOpenViewDialog(true);
  };

  const handleOpenDuplicateDialog = (widget) => {
    setSelectedWidget(widget);
    setOpenDuplicateDialog(true);
  };

  const handleCloseDuplicateDialog = (refresh?: boolean) => {
    setSelectedWidget(null);
    setOpenDuplicateDialog(false);
    // Data will be refetched automatically when dependencies change
  };

  const handleCloseDetailModal = () => {
    setSelectedWidget(null);
    setOpenViewDialog(false);
  };

  const goToEdit = (widgetId) => {
    navigate(ROUTES.WIDGET_EDIT.replace(':widgetId', widgetId));
  };

  // Calc total page
  const totalPage = useMemo(() => {
    const pages = Math.ceil(totalCount / perPage);
    console.log('Pagination debug:', { totalCount, perPage, totalPage: pages, widgetsLength: widgets.length });
    return pages;
  }, [totalCount, perPage, widgets.length]);

  // Page change handler
  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    console.log('Page changed to:', page);
    setCurrentPage(page);
    updateUrlParams({ page });
  };

  const gotoAddWidget = () => {
    navigate(ROUTES.WIDGET_ADD);
  };

  // Check if user can add widgets
  const canAddWidget = useMemo(() => {
    if (isAdmin) {
      // Admin can always add widgets, or check selected customer's permission
      if (customer) {
        return customer.canAddWidget !== false;
      }
      return true; // Admin can add when no customer selected
    }
    // For regular users, check their own permission
    return account?.canAddWidget !== false;
  }, [isAdmin, customer, account]);

  // Check if user can remove widgets
  const canRemoveWidget = useMemo(() => {
    if (isAdmin) {
      // For admin users, check the selected customer's permission
      if (customer) {
        return customer.canRemoveWidget !== false;
      }
      return true; // Admin can remove when no customer selected
    }
    // For regular users, check their own permission
    return account?.canRemoveWidget !== false;
  }, [isAdmin, customer, account]);

  // Get the widget delete password flag for the current user/customer
  const hasProtectionPassword = useMemo(() => {
    if (isAdmin && customer) {
      return !!(customer.hasProtectionPassword || customer.hasWidgetDeletePassword);
    }
    return !!(account?.hasProtectionPassword || account?.hasWidgetDeletePassword);
  }, [isAdmin, customer, account]);

  const handleDeleteWidget = (id: string) => {
    // Admins don't need password confirmation, even if password is set
    const needsPassword = !isAdmin && hasProtectionPassword;
    
    activeAlert({
      type: 'info',
      text: "Are you sure to remove this widget?",
      requirePassword: needsPassword,
    }, (password?: string) => {
      setLoading(true);
      // For non-admin users, use account ID; for admin users, use selected customer ID
      const customerId = isAdmin ? customer?.id : account?.id;
      apis.cdn.removeCdnWidgetByWidgetId(id, customerId, password)
      .then((res) => {
        if (res.status) {
          activeToastRef.current({
            type: 'success',
            text: res.msg,
          });
          // Refetch data with current params
          fetchWidgets();
        } else {
          // Handle case where API returns status: false (e.g., invalid password)
          activeToastRef.current({
            type: 'error',
            text: res.msg || 'Failed to delete widget. Please try again.',
          });
        }
      })
      .catch((err) => {
        activeToastRef.current({
          type: 'error',
          text: err.msg || 'Failed to delete widget. Please try again.',
        });
      })
      .finally(() => {
        setLoading(false);
      });
    });
  };

  const handleDeleteWidgetForever = async (id: string) => {
    const needsPassword = !isAdmin && hasProtectionPassword;
    activeAlert({
      type: 'info',
      text: "Are you sure to remove this widget forever?",
      requirePassword: needsPassword,
    }, async (password?: string) => {
      try {
        const customerId = isAdmin ? customer?.id : account?.id;
        const res = await apis.cdn.removeForeverCdnWidgetByWidgetId(id, customerId, password);
        if (res.status === false) {
          activeToastRef.current({
            type: 'error',
            text: res.msg || 'Failed to delete widget. Please try again.',
          });
          return;
        }
        activeToastRef.current({
          type: 'success',
          text: res.msg,
        });
        fetchWidgets();
      } catch (e: any) {
        activeToastRef.current({
          type: 'error',
          text: e?.msg || 'Failed to delete widget. Please try again.',
        });
      }
    });
  };

  const handleRecoverWidget = async (id: string) => {
    activeAlert({
      type: 'info',
      text: "Are you sure to recover this widget?",
    }, async () => {
      try {
        const res = await apis.cdn.recoverCdnWidgetByWidgetId(id);
        activeToastRef.current({
          type: 'success',
          text: res.msg,
        });
        // Data will be refetched automatically when dependencies change
      } catch (e) {
        activeToastRef.current({
          type: 'error',
          text: 'Failed to recover widget. Please try again.',
        });
      }
    });
  };

  // Sort handler
  const handleSort = (value: ISortBy) => {
    setSort(value);
    updateUrlParams({ sort: `${value.field}_${value.order}` });
  };

  // Update URL params when showDeleted changes
  useEffect(() => {
    if (isInitialized) {
      setCurrentPage(1);
      updateUrlParams({ page: 1, archived: showDeleted });
    }
  }, [showDeleted, isInitialized]);



  // Define columns
  const columns: IColumn[] = [
    {
      field: 'index',
      label: '#',
      sortable: false,
      render: (row) => {
        const rowIndex = widgets.findIndex(widget => widget._id === row._id);
        return (dataPage - 1) * perPage + rowIndex + 1;
      },
    },
    {
      field: 'name',
      label: 'Name',
    },
    {
      field: '_id',
      label: 'Widget ID',
      sortable: false,
    },
    ...(isAdmin ? [{
      field: 'customer',
      label: 'Customer Name',
      sortable: false,
      render: ({ customer }) => `${customer?.firstName} ${customer?.lastName}`,
    }] : []),
    {
      field: 'isin',
      label: 'ISIN',
    },
    {
      field: 'createdAt',
      label: 'Creation Date',
      render: (row) => moment(row.createdAt).format('DD/MM/YYYY HH:mm'),
    },
    {
      field: 'updatedAt',
      label: 'Modification Date',
      render: (row) => moment(row.updatedAt).format('DD/MM/YYYY HH:mm'),
    },
    ...(showDeleted ? [{
      field: 'delete_after',
      label: 'Will be deleted after',
      render: (row) => {
        const deletedAfter = retentionConfig.widget - moment().diff(row.deletedAt, 'day');
        return `${deletedAfter > 1 ? `after ${deletedAfter} days` : deletedAfter === 1 ? 'tomorrow' : '' }`;
      }
    }] : []),
    {
      field: 'actions',
      label: 'Actions',
      sortable: false,
      render: (row) => (
        <Box display="flex">
          {showDeleted ? (
            <>
              <Tooltip title="Recover">
                {/*@ts-ignore*/}
                <IconButton
                  variant="text"
                  size="medium"
                  onClick={() => handleRecoverWidget(row._id)}
                >
                  <RecoverIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Forever">
                {/*@ts-ignore*/}
                <IconButton
                  variant="text"
                  size="medium"
                  onClick={() => handleDeleteWidgetForever(row._id)}
                >
                  <DeleteForeverIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip title="Duplicate">
                {/*@ts-ignore*/}
                <IconButton
                  variant="text"
                  size="medium"
                  onClick={() => handleOpenDuplicateDialog(row)}
                >
                  <ContentCopy />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                {/*@ts-ignore*/}
                <IconButton
                  variant="text"
                  size="medium"
                  onClick={() => goToEdit(row._id)}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
              <Tooltip title="View">
                {/*@ts-ignore*/}
                <IconButton
                  variant="text"
                  size="medium"
                  onClick={() => handleOpenDetailModal(row)}
                >
                  <Visibility />
                </IconButton>
              </Tooltip>
              <Tooltip title={canRemoveWidget ? "Delete" : "Delete disabled - permission not allowed"}>
                {/*@ts-ignore*/}
                <IconButton
                  variant="text"
                  size="medium"
                  onClick={() => handleDeleteWidget(row._id)}
                  disabled={!canRemoveWidget}
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" marginBottom={20} alignItems="center">
        {isAdmin && <UserSelector value={customer} onChange={handleCustomerChange} />}
        {canAddWidget && (
          <Button startIcon={<PlusIcon />} onClick={gotoAddWidget}>Add New Widget</Button>
        )}
      </Box>

      <Stack alignItems="center" spacing={{ xs: 36, md: 66 }}>
        <Table
          columns={columns}
          data={widgets}
          order={sort.order}
          orderBy={sort.field}
          isLoading={loading}
          onSort={handleSort}
          contentSize={{
            width: {
              xs: 'calc(100vw - 40px)',
              sm: 'calc(100vw - 80px)',
              md: 'calc(100vw - 256px)',
              lg: 'calc(100vw - 376px)',
            },
          }}
        />
        {(totalPage > 1 || totalCount > perPage) && (
          <Pagination 
            page={currentPage} 
            count={totalPage} 
            siblingCount={0} 
            onChange={handlePageChange}
            disabled={loading}
          />
        )}
      </Stack>

      {!!selectedWidget && openViewDialog && (
        <WidgetPreviewDialog
          open={openViewDialog}
          selectedWidget={selectedWidget}
          onClose={handleCloseDetailModal}
        />
      )}

      {!!selectedWidget && selectedWidget && (
        <DuplicateWidget
          open={openDuplicateDialog}
          selectedWidget={selectedWidget}
          onClose={handleCloseDuplicateDialog}
        />
      )}
    </Box>
  )
};

